import type { IconDefinition } from '@fortawesome/free-solid-svg-icons'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Chip, Stack, Typography } from '@mui/material'
import type { FC, ReactNode } from 'react'
import { useState } from 'react'

import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import { GuideMeBanner } from '@/components/IncomeCard/GuideMeBanner'
import {
  HeaderTextFlex,
  SubheaderText,
  Text,
} from '@/components/IncomeCard/TrustedSources/styles'
import { Colours } from '@/utils/styles'

import type { OptOutReasonValue } from './OptOutReasonSection'
import OptOutReasonSection from './OptOutReasonSection'
import type { ConnectionFailure } from './TechnicalIssuesSection'
import TechnicalIssuesSection from './TechnicalIssuesSection'

const IconWrapper = {
  marginRight: '8px',
  display: 'inline-block',
}

const FAQContainer = {
  marginTop: '40px',
}

export interface FAQ {
  question: string
  answer: string
}

export interface GuideMeConfig {
  sidebarTitle: string
  intro: ReactNode
  faqs: FAQ[]
  bannerCopy: string
}

const StatusHeader: FC<{ icon: IconDefinition; title: string; isConnected: boolean }> = ({
  icon,
  title,
  isConnected,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '14px',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <FontAwesomeIcon icon={icon} color="#082842" size="lg" />
      <HeaderTextFlex variant="h6">{title}</HeaderTextFlex>
    </Box>
    <Chip
      label={isConnected ? 'Connected' : 'Not connected'}
      sx={[
        {
          fontWeight: 600,
          fontSize: '14px',
          borderRadius: '5px',
          '& .MuiChip-label': {
            padding: '5px 10px',
          },
        },
        isConnected
          ? {
              backgroundColor: '#e0f2d7',
              color: '#2a4b19',
            }
          : {
              backgroundColor: '#fee5e6',
              color: '#79050b',
            },
      ]}
    />
  </Box>
)

const CommentsSection: FC<{ title?: string; comments: string[] }> = ({
  title = 'Comments',
  comments,
}) => (
  <Box>
    <SubheaderText variant="subtitle2">{title}</SubheaderText>
    <Box
      sx={{
        background: Colours.lightGrey,
        borderRadius: '10px',
        padding: '12px 16px',
      }}
    >
      {comments.map((comment, i) => (
        <Text key={i} variant="body2">
          {comment}
        </Text>
      ))}
    </Box>
  </Box>
)

const GuideMeSidebar: FC<{ config: GuideMeConfig; onClose: () => void }> = ({
  config,
  onClose,
}) => (
  <SlideInSidebar
    onClose={onClose}
    header={<h1>{config.sidebarTitle}</h1>}
    content={
      <div>
        <div>
          <div style={IconWrapper}>
            <FontAwesomeIcon
              color={'#34D9C3'}
              icon={faInfoCircle}
              size="lg"
              data-testid="icon"
              className="info-icon"
            />
          </div>
          {config.intro}
        </div>
        <div style={FAQContainer}>
          <h1>FAQ</h1>
          {config.faqs.map((faq, i) => (
            <div key={i}>
              <h2>{faq.question}</h2>
              <p>{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>
    }
  />
)

export interface TrustedSourceCardProps {
  icon: IconDefinition
  title: string
  isConnected: boolean
  isShortEmployment?: boolean
  guideMe?: GuideMeConfig
  headerContent?: ReactNode
  statusMessage: string
  optOutReason?: OptOutReasonValue | null
  getOptOutReasonText?: (category: string) => string
  showAdditionalInformationOnlyFor?: string
  hasOutcome: boolean
  comments?: string[]
  connectionFailures?: ConnectionFailure[]
  outcomeContent?: ReactNode
  actionLink?: ReactNode
}

export const TrustedSourceCard: FC<TrustedSourceCardProps> = ({
  icon,
  title,
  isConnected,
  isShortEmployment,
  guideMe,
  headerContent,
  statusMessage,
  optOutReason,
  getOptOutReasonText,
  showAdditionalInformationOnlyFor,
  hasOutcome,
  comments,
  connectionFailures,
  outcomeContent,
  actionLink,
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false)

  return (
    <Stack spacing={2}>
      <StatusHeader icon={icon} title={title} isConnected={isConnected} />

      {headerContent}

      {isSidebarOpen && guideMe && (
        <GuideMeSidebar config={guideMe} onClose={() => setIsSidebarOpen(false)} />
      )}

      {isShortEmployment && guideMe && (
        <GuideMeBanner copy={guideMe.bannerCopy} onClick={() => setIsSidebarOpen(true)} />
      )}

      <Typography
        variant="body1"
        sx={{
          fontSize: '14px',
          color: '#082842',
          margin: 0,
        }}
      >
        {statusMessage}
      </Typography>

      {hasOutcome ? (
        <Stack spacing={2.5}>
          {comments && comments.length > 0 && <CommentsSection comments={comments} />}
          {outcomeContent}
          {actionLink}
        </Stack>
      ) : (
        <>
          {optOutReason && getOptOutReasonText && (
            <OptOutReasonSection
              optOutReason={optOutReason}
              getReasonText={getOptOutReasonText}
              showAdditionalInformationOnlyFor={showAdditionalInformationOnlyFor}
            />
          )}
          {connectionFailures && connectionFailures.length > 0 && (
            <TechnicalIssuesSection failures={connectionFailures} />
          )}
          {actionLink}
        </>
      )}
    </Stack>
  )
}

export default TrustedSourceCard
